<script setup lang="ts">
import { ref, reactive, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import axios from 'axios'
import type { Invoice, ProviderType, PartnerOffer, PartnerOfferTerm } from '@/types'
import { initYandexMetrika } from '@/helpers/yandexMetrika'

import AppHeader from '@/layouts/AppHeader/AppHeader.vue'
import AppBanner from '@/layouts/AppBanner/AppBanner.vue'
import UiSection from '@/components/UiSection/UiSection.vue'
import UiFooter from '@/components/UiFooter/UiFooter.vue'
import VpnWarningBanner from '@/components/VpnWarningBanner/VpnWarningBanner.vue'
import PaymentOptionCard from '@/components/PaymentOptionCard/PaymentOptionCard.vue'
import Step2OptionCard from '@/components/Step2OptionCard/Step2OptionCard.vue'
import PartnerTermCard from '@/components/PartnerTermCard/PartnerTermCard.vue'
import SwitchField from '@/components/SwitchField/SwitchField.vue'
import ProviderButtonCard from '@/components/ProviderButtonCard/ProviderButtonCard.vue'
import PaymentProviderCard from '@/components/PaymentProviderCard/PaymentProviderCard.vue'

import kreditImg from '@/assets/img/kredit.svg'
import rassrockaImg from '@/assets/img/rassrocka.svg'
import paymentImg from '@/assets/img/one-payment.svg'
import payDomesticImg from '@/assets/img/pay-domestic.svg'
import payForeignImg from '@/assets/img/pay-foreign.svg'

type UpsaleKey =
  | 'sms_notification'
  | 'extended_insurance'
  | 'installment_conditions'
  | 'basic_insurance'
  | 'premium_insurance'
  | 'subsidized_installment'

// ---------------- STATE ----------------
const route = useRoute()
const invoice = ref<Invoice | null>(null)

const offerAccepted = ref(true)
const offerError = ref(false)
const installmentError = ref(false)
const isLoading = ref(false)
const pageLoader = ref(true)

const currentStep = ref(1)
const selectedType = ref<ProviderType['slug'] | null>(null)
const externalLink = ref<{ url: string; logo_url: string | null; desc: string | null } | null>(null)

// Партнёрский офер (Т-Банк) плашкой в «Купить в рассрочку»: заявка создаётся
// из данных счёта, дальше выбор срока (если сроков несколько) и переход в банк.
const partnerOffer = computed<PartnerOffer | null>(() => invoice.value?.partner_offer ?? null)
const partnerTerms = ref<PartnerOfferTerm[]>([])
const partnerRequestId = ref<string | null>(null)
const showPartnerTerms = computed(() => partnerTerms.value.length > 0)

async function startPartnerOffer() {
  if (!invoice.value || !partnerOffer.value || isLoading.value) return

  try {
    isLoading.value = true
    const { data } = await axios.post<{ redirect_url?: string; request_id?: string; terms?: PartnerOfferTerm[] }>(
      '/l/from-invoice',
      { invoice_id: invoice.value.id, offer_id: partnerOffer.value.id }
    )

    if (data.redirect_url) {
      window.location.href = data.redirect_url
      return
    }

    if (data.terms?.length) {
      partnerRequestId.value = data.request_id ?? null
      partnerTerms.value = [...data.terms].sort((a, b) => a.sort_order - b.sort_order || a.term_months - b.term_months)
    }
  } catch (error) {
    console.error('Ошибка при переходе к партнёрскому оферу:', error)
  } finally {
    isLoading.value = false
  }
}

async function choosePartnerTerm(term: PartnerOfferTerm) {
  try {
    isLoading.value = true
    const { data } = await axios.post<{ redirect_url: string }>('/l/choose-term', {
      request_id: partnerRequestId.value,
      term_id: term.id
    })
    window.location.href = data.redirect_url
  } catch (error) {
    console.error('Ошибка при выборе срока:', error)
    isLoading.value = false
  }
}

function closePartnerTerms() {
  partnerTerms.value = []
  partnerRequestId.value = null
}
const selectedProvider = ref<any>(null)
const selectedBnplOption = ref<any>(null)
const selectedPaymentOption = ref<any>(null)

const UPSALE_META: Record<
  UpsaleKey,
  {
    defaultValue: boolean
    disabled?: boolean
  }
> = {
  sms_notification: {
    defaultValue: true
  },
  extended_insurance: {
    defaultValue: true
  },
  installment_conditions: {
    defaultValue: false
  },
  basic_insurance: {
    defaultValue: false
  },
  premium_insurance: {
    defaultValue: false
  },
  subsidized_installment: {
    defaultValue: false,
    disabled: true
  }
}

const upsalesState = reactive<Record<UpsaleKey, boolean>>({
  sms_notification: false,
  extended_insurance: true,
  installment_conditions: false,
  basic_insurance: false,
  premium_insurance: false,
  subsidized_installment: false
})

const availableUpsales = computed<UpsaleKey[]>(() => {
  const raw = selectedProvider.value?.upsales ?? invoice.value?.upsales ?? []
  return raw.filter((key: string): key is UpsaleKey => key in UPSALE_META)
})

function applyUpsaleDefaults(keys: UpsaleKey[]) {
  keys.forEach((key) => {
    if (availableUpsales.value.includes(key)) {
      upsalesState[key] = UPSALE_META[key].defaultValue
    }
  })
}

watch(
  availableUpsales,
  (keys) => {
    keys.forEach((key) => {
      if (key === 'subsidized_installment') return
      upsalesState[key] = UPSALE_META[key].defaultValue
    })
  },
  { immediate: true }
)

watch(
  [selectedType, currentStep, availableUpsales],
  () => {
    const shouldEnableSubsidized =
      selectedType.value === 'rassrocka' &&
      currentStep.value === 4 &&
      availableUpsales.value.includes('subsidized_installment')

    upsalesState.subsidized_installment = shouldEnableSubsidized
  },
  { immediate: true }
)

const visibleUpsales = computed<UpsaleKey[]>(() => {
  if (!invoice.value) return []

  // КРЕДИТ — показываем sms + страховку
  if (selectedType.value === 'kredit' && currentStep.value === 3) {
    return availableUpsales.value.filter((key) =>
      ['sms_notification', 'extended_insurance'].includes(key)
    )
  }

  // ЕДИНОРАЗОВЫЙ ПЛАТЁЖ — только страховка 2500
  if (selectedType.value === 'edinorazovyi-platez' && currentStep.value === 3) {
    return availableUpsales.value.filter((key) => ['extended_insurance'].includes(key))
  }

  // РАССРОЧКА — финальный шаг
  if (selectedType.value === 'rassrocka' && currentStep.value === 4) {
    return availableUpsales.value.filter((key) =>
      ['sms_notification', 'extended_insurance', 'subsidized_installment'].includes(key)
    )
  }

  return []
})

// ---------------- API ----------------
async function getPayment(uuid: string | string[]) {
  const response = await axios.get<Invoice>(`/${uuid}`)
  invoice.value = response.data

  if (invoice.value.success_url) {
    window.location.href = invoice.value.success_url
  }

  if (invoice.value.yandex_metrika_id) {
    initYandexMetrika(invoice.value.yandex_metrika_id)
  }

  const hasRassrocka = invoice.value.providers.some((p) => p.type.slug === 'rassrocka')
  if (hasRassrocka) {
    try {
      const res = await axios.get<{
        url: string | null
        logo_url: string | null
        desc: string | null
      }>('/integrations/external-link')
      const data = res.data
      externalLink.value = data.url
        ? { url: data.url, logo_url: data.logo_url, desc: data.desc }
        : null
    } catch {
      externalLink.value = null
    }
  }
}

function onPageShow(e: PageTransitionEvent) {
  if (e.persisted) isLoading.value = false
}

onMounted(async () => {
  try {
    await getPayment(route.params.uuid)
  } finally {
    pageLoader.value = false
  }
  window.addEventListener('pageshow', onPageShow)

  if (providerTypes.value.length === 1) {
    selectType(providerTypes.value[0].slug)
  }
})

onUnmounted(() => {
  window.removeEventListener('pageshow', onPageShow)
})

// ---------------- PROVIDER TYPES ----------------
const providerTypes = computed(() => {
  if (!invoice.value) return []
  const map = new Map<string, ProviderType>()
  let hasOplata = false

  invoice.value.providers.forEach((p) => {
    if (p.type.slug === 'oplata-iz-za-rubeza') {
      hasOplata = true
      return
    }
    if (!map.has(p.type.slug)) map.set(p.type.slug, p.type)
  })

  // Если есть провайдер оплаты из-за рубежа, но нет обычного единоразового —
  // добавляем синтетическую запись, чтобы она появилась на шаге 1
  if (hasOplata && !map.has('edinorazovyi-platez')) {
    map.set('edinorazovyi-platez', {
      id: 'synthetic-edinorazovyi-platez',
      name: 'Единоразовый платёж',
      slug: 'edinorazovyi-platez'
    } as ProviderType)
  }

  return Array.from(map.values())
})

// ---------------- СУБСИДИРОВАННАЯ РАССРОЧКА ----------------
const hasSubsidizedInstallment = computed(() => {
  if (!invoice.value) return false

  const rassrockaProviders = invoice.value.providers.filter((p) => p.type.slug === 'rassrocka')

  return rassrockaProviders.some(
    (p) => p.buttons?.some((b) => Number(b.subsidized_installment_percent) > 0)
  )
})

const hasBnpl = computed(() => {
  if (!invoice.value) return false

  return invoice.value.providers.some(
    (p) => p.type.slug === 'rassrocka' && p.credit_instrument_type === 'bnpl'
  )
})

const hasOplataIzZaRubeza = computed(() => {
  if (!invoice.value) return false
  return invoice.value.providers.some((p) => p.type.slug === 'oplata-iz-za-rubeza')
})

const hasDomesticEdinorazovyi = computed(() => {
  if (!invoice.value) return false
  return invoice.value.providers.some((p) => p.type.slug === 'edinorazovyi-platez')
})

// ---------------- FILTERED PROVIDERS ----------------
const filteredProviders = computed(() => {
  if (!invoice.value || !selectedType.value) return []

  // ===== ЕДИНОРАЗОВЫЙ ПЛАТЁЖ =====
  if (selectedType.value === 'edinorazovyi-platez') {
    if (
      !selectedPaymentOption.value &&
      hasDomesticEdinorazovyi.value &&
      hasOplataIzZaRubeza.value
    ) {
      return [
        {
          id: 'domestic',
          type: 'domestic',
          title: 'Оплата картой российского банка в Рублях, ЮMoney, SberPay, СБП',
          logo: payDomesticImg
        },
        {
          id: 'foreign',
          type: 'foreign',
          title: 'Оплата картой зарубежного банка в USD, EUR и других иностранных валютах',
          logo: payForeignImg
        }
      ]
    }

    if (selectedPaymentOption.value?.type === 'foreign') {
      return invoice.value.providers.filter((p) => p.type.slug === 'oplata-iz-za-rubeza')
    }

    return invoice.value.providers.filter((p) => p.type.slug === 'edinorazovyi-platez')
  }

  // ===== РАССРОЧКА =====
  if (selectedType.value === 'rassrocka') {
    const rassrockaProviders = invoice.value.providers.filter((p) => p.type.slug === 'rassrocka')

    if (!selectedBnplOption.value) {
      const hasInstallment = rassrockaProviders.some(
        (p) => !p.credit_instrument_type || p.credit_instrument_type === 'installment'
      )
      const hasBnpl = rassrockaProviders.some((p) => p.credit_instrument_type === 'bnpl')

      const result: any[] = []

      if (hasInstallment) {
        result.push({
          id: 'installment',
          type: 'installment',
          title: 'Рассрочка <b>без первого взноса</b>, переплаты и стандартной одобряемостью'
        })
      }

      if (hasBnpl) {
        result.push({
          id: 'bnpl',
          type: 'bnpl',
          title: 'Рассрочка <b>с первым взносом</b> и повышенной одобряемостью (BNPL)'
        })
      }

      return result
    }

    return rassrockaProviders.filter((p) => {
      const type = p.credit_instrument_type || 'installment'
      return type === selectedBnplOption.value.type
    })
  }

  // ===== КРЕДИТ =====
  return invoice.value.providers.filter((p) => p.type.slug === selectedType.value)
})

// ---------------- NAVIGATION ----------------
const canGoBack = computed(() => {
  const multipleTypes = providerTypes.value.length > 1

  if (selectedType.value === 'edinorazovyi-platez') {
    if (currentStep.value === 2) return multipleTypes
    if (currentStep.value === 3) {
      if (hasDomesticEdinorazovyi.value && hasOplataIzZaRubeza.value) return true
      return multipleTypes
    }
    return multipleTypes
  }

  if (selectedType.value === 'kredit') {
    if (currentStep.value === 2) return multipleTypes
    if (currentStep.value === 3) {
      // назад на шаг 2 (если провайдеров > 1) или на шаг 1
      return filteredProviders.value.length > 1 || multipleTypes
    }
  }

  if (selectedType.value === 'rassrocka') {
    if (currentStep.value === 2) return multipleTypes
    if (currentStep.value === 3) {
      // если есть BNPL — шаг 2 был показан (2 опции), назад туда
      if (hasBnpl.value) return true
      return multipleTypes
    }
    if (currentStep.value === 4) {
      // назад на шаг 3 (если провайдеров > 1)
      if (filteredProviders.value.length > 1) return true
      // шаг 3 был пропущен — проверяем шаг 2
      if (hasBnpl.value) return true
      // шаг 2 тоже пропущен — проверяем шаг 1
      return multipleTypes
    }
  }

  return false
})

function selectType(typeSlug: ProviderType['slug']) {
  selectedType.value = typeSlug
  selectedProvider.value = null
  selectedBnplOption.value = null
  selectedPaymentOption.value = null

  // единоразовый платеж
  if (typeSlug === 'edinorazovyi-platez') {
    applyUpsaleDefaults(['extended_insurance'])

    if (hasDomesticEdinorazovyi.value && hasOplataIzZaRubeza.value) {
      currentStep.value = 2
      return
    }

    selectedPaymentOption.value = hasOplataIzZaRubeza.value
      ? { type: 'foreign' }
      : { type: 'domestic' }
    currentStep.value = 3
    if (filteredProviders.value.length === 1) {
      selectProvider(filteredProviders.value[0], true)
    }
    return
  }

  // рассрочка
  if (typeSlug === 'rassrocka') {
    if (hasSubsidizedInstallment.value && !hasBnpl.value) {
      selectedBnplOption.value = { type: 'installment' }
      currentStep.value = 3
      if (filteredProviders.value.length === 1) {
        selectProvider(filteredProviders.value[0], true)
      }
      return
    }

    if (!hasBnpl.value) {
      selectedBnplOption.value = { type: 'installment' }
      currentStep.value = 3
      if (filteredProviders.value.length === 1) {
        selectProvider(filteredProviders.value[0], true)
      }
      return
    }

    currentStep.value = 2
    return
  }

  // КРЕДИТ
  if (typeSlug === 'kredit') {
    currentStep.value = 2
    if (filteredProviders.value.length === 1) {
      selectProvider(filteredProviders.value[0], true)
    }
    return
  }
}

function selectProvider(provider: any, fromAutoFlow = false) {
  if (
    selectedType.value === 'edinorazovyi-platez' &&
    !selectedPaymentOption.value &&
    hasDomesticEdinorazovyi.value &&
    hasOplataIzZaRubeza.value
  ) {
    selectedPaymentOption.value = provider
    currentStep.value = 3
    if (filteredProviders.value.length === 1) {
      selectProvider(filteredProviders.value[0], fromAutoFlow)
    }
  } else if (selectedType.value === 'rassrocka' && !selectedBnplOption.value && hasBnpl.value) {
    selectedBnplOption.value = provider
    currentStep.value = 3
    if (filteredProviders.value.length === 1) {
      selectProvider(filteredProviders.value[0], fromAutoFlow)
    }
  } else {
    selectedProvider.value = provider

    if (selectedType.value === 'rassrocka') {
      currentStep.value = 4
      applyUpsaleDefaults(['sms_notification', 'extended_insurance'])
    } else if (selectedType.value === 'kredit') {
      currentStep.value = 3
      applyUpsaleDefaults(['sms_notification', 'extended_insurance'])
    } else {
      currentStep.value = 3
      applyUpsaleDefaults(['extended_insurance'])
    }

    // Авто-сабмит: весь путь был без выбора пользователя (1 тип, 1 провайдер, 1 срок, нет допов)
    if (
      fromAutoFlow &&
      providerTypes.value.length === 1 &&
      provider.buttons?.length === 1 &&
      visibleUpsales.value.length === 0 &&
      !showOfferCheckbox.value
    ) {
      submitAction(provider.buttons[0])
    }
  }
}

function goBack() {
  // Единоразовый платёж
  if (selectedType.value === 'edinorazovyi-platez') {
    if (currentStep.value === 3 && hasDomesticEdinorazovyi.value && hasOplataIzZaRubeza.value) {
      currentStep.value = 2
      selectedProvider.value = null
      selectedPaymentOption.value = null
      return
    }

    currentStep.value = 1
    selectedType.value = null
    selectedProvider.value = null
    selectedBnplOption.value = null
    selectedPaymentOption.value = null
    return
  }

  if (selectedType.value === 'kredit') {
    if (currentStep.value === 3) {
      selectedProvider.value = null
      // если провайдер был один — шаг 2 был пропущен, идём на шаг 1
      if (filteredProviders.value.length === 1) {
        currentStep.value = 1
        selectedType.value = null
      } else {
        currentStep.value = 2
      }
      return
    }

    if (currentStep.value === 2) {
      currentStep.value = 1
      selectedType.value = null
      selectedProvider.value = null
      return
    }
  }

  if (selectedType.value === 'rassrocka') {
    if (currentStep.value === 4) {
      selectedProvider.value = null
      // если провайдер был один — шаг 3 был пропущен
      if (filteredProviders.value.length === 1) {
        if (hasBnpl.value) {
          // шаг 2 был показан (выбор BNPL/installment) — идём туда
          currentStep.value = 2
          selectedBnplOption.value = null
        } else {
          // шаг 2 тоже был пропущен — идём на шаг 1
          currentStep.value = 1
          selectedType.value = null
          selectedBnplOption.value = null
        }
      } else {
        currentStep.value = 3
      }
      return
    }

    if (currentStep.value === 3) {
      // если BNPL отсутствует — шаг 2 был пропущен
      if (!hasBnpl.value) {
        currentStep.value = 1
        selectedType.value = null
        selectedProvider.value = null
        selectedBnplOption.value = null
        return
      }

      // обычный сценарий (есть BNPL)
      currentStep.value = 2
      selectedBnplOption.value = null
      return
    }

    if (currentStep.value === 2) {
      currentStep.value = 1
      selectedType.value = null
      selectedProvider.value = null
      selectedBnplOption.value = null
      return
    }
  }

  if (currentStep.value > 1) currentStep.value--
  if (currentStep.value === 1) selectedType.value = null
}

// ---------------- SUBMIT ----------------
async function submitAction(button: any) {
  if (showOfferCheckbox.value && !offerAccepted.value) {
    offerError.value = true
    return
  } else {
    offerError.value = false
  }

  if (
    selectedType.value === 'rassrocka' &&
    currentStep.value === 4 &&
    availableUpsales.value.includes('subsidized_installment')
  ) {
    if (!upsalesState.subsidized_installment) {
      installmentError.value = true
      return
    } else {
      installmentError.value = false
    }
  }

  if (!invoice.value?.id) return

  isLoading.value = true

  try {
    const upsalesEntries = availableUpsales.value.map((type) => [type, upsalesState[type]] as const)

    await Promise.all(
      upsalesEntries.map(([type, enabled]) =>
        axios.post(`/${route.params.uuid}/upsales/${type}`, { enabled })
      )
    )

    // Задержка на всякий из-за проблем со странным поведением апсейлов
    await new Promise((resolve) => setTimeout(resolve, 300))

    const enabledUpsales = upsalesEntries.filter(([_, enabled]) => enabled).map(([type]) => type)

    let response
    if (button.method === 'POST') {
      response = await axios.post(button.action)
    } else {
      const params = new URLSearchParams({
        upsales: JSON.stringify(enabledUpsales)
      }).toString()
      response = await axios.get(`${button.action}?${params}`)
    }

    if (response?.data?.link) {
      window.location.href = response.data.link
    }
  } catch (error) {
    console.error('Ошибка при submitAction:', error)
    isLoading.value = false
  }
}

function openExternalLink() {
  if (externalLink.value?.url) {
    window.open(externalLink.value.url, '_blank', 'noopener,noreferrer')
  }
}

// ---------------- TITLES ----------------
const stepTitles = computed(() => {
  if (!selectedType.value) {
    return {
      title: 'Выберите способ оплаты',
      subtitle:
        'Все платежи осуществляются через защищённые платёжные системы и надёжные банковские организации'
    }
  }

  if (selectedType.value === 'edinorazovyi-platez') {
    if (currentStep.value === 2) {
      return {
        title: 'Выберите способ оплаты',
        subtitle:
          'Все платежи осуществляются через защищённые платёжные системы и надёжные банковские организации'
      }
    }

    if (selectedPaymentOption.value?.type === 'foreign') {
      return {
        title: 'Оплата картой зарубежного банка в USD, EUR и других иностранных валютах',
        subtitle:
          'Все платежи осуществляются через защищённые платёжные системы и надёжные банковские операции'
      }
    }

    return {
      title: 'Оплата картой российского банка в Рублях, ЮMoney, SberPay, СБП',
      subtitle:
        'Все платежи осуществляются через защищённые платёжные системы и надёжные банковские операции'
    }
  }

  if (selectedType.value === 'rassrocka') {
    if (hasSubsidizedInstallment.value) {
      if (!selectedBnplOption.value) {
        return {
          title: 'Рассрочка без первого взноса от надёжных банков и брокеров',
          subtitle:
            'Мы показываем банки и кредитные организации в порядке вероятности одобрения. Начните с первого — так вы сэкономите время и повысите шанс успешного оформления.'
        }
      }

      if (currentStep.value === 3) {
        const isBnpl = selectedBnplOption.value?.type === 'bnpl'
        return {
          title: isBnpl
            ? 'Рассрочка с первым взносом от надержных финансовых организаций'
            : 'Рассрочка без первого взноса и переплаты процентов',
          subtitle:
            'Мы показываем банки и кредитные организации в порядке вероятности одобрения. Начните с первого — так вы сэкономите время и повысите шанс успешного оформления.'
        }
      }

      if (currentStep.value === 4) {
        return {
          title: 'Выберите комфортный срок и платёж для вашей рассрочки',
          subtitle:
            'После выбора срока вы перейдёте к заполнению основной анкеты. Обычно это занимает не более 3-5 минут.'
        }
      }
    } else {
      if (!selectedBnplOption.value) {
        return {
          title: 'Выберите удобный вариант рассрочки',
          subtitle:
            'Все рассрочки предоставляются банками или финансовыми организациями с наивысшим рейтингом надёжности'
        }
      }

      if (currentStep.value === 3) {
        const isBnpl = selectedBnplOption.value?.type === 'bnpl'
        return {
          title: isBnpl
            ? 'Рассрочка с первым взносом от надержных финансовых организаций'
            : 'Рассрочка без первого взноса и переплаты процентов',
          subtitle:
            'Мы показываем банки и кредитные организации в порядке вероятности одобрения. Начните с первого — так вы сэкономите время и повысите шанс успешного оформления.'
        }
      }

      if (currentStep.value === 4) {
        return {
          title: 'Выберите комфортный срок и платёж для вашей рассрочки',
          subtitle:
            'После выбора срока вы перейдёте к заполнению основной анкеты. Обычно это занимает не более 3-5 минут.'
        }
      }
    }
  }

  if (selectedType.value === 'kredit') {
    if (!selectedProvider.value) {
      return {
        title: 'Оформить кредит в надёжном банке или через популярного брокера',
        subtitle:
          'Мы показываем банки и кредитные организации в порядке вероятности одобрения. Начните с первого — так вы сэкономите время и повысите шанс успешного оформления.'
      }
    }

    if (selectedProvider.value) {
      return {
        title: 'Выберите комфортный срок, срок и платёж кредита',
        subtitle:
          'После выбора срока вы перейдёте к заполнению основной анкеты. Обычно это занимает не более 3-5 минут.'
      }
    }
  }

  return { title: '', subtitle: '' }
})

function getStep1Title(slug: string) {
  if (slug === 'kredit') return 'Купить в кредит'
  if (slug === 'rassrocka') return 'Купить в рассрочку'
  if (slug === 'edinorazovyi-platez') return 'Оплатить всю сумму сразу'
  return ''
}

function getStep1Description(slug: string) {
  if (slug === 'kredit') {
    return 'Оформите кредит, если сумма заказа превышает параметры предлагаемых рассрочек, либо вы просто хотите уменьшить платёж за счёт большего срока погашения'
  }

  if (slug === 'rassrocka') {
    const subsidized = hasSubsidizedInstallment.value
    if (subsidized) return 'Оформите банковскую рассрочку, чтобы оплатить покупку частями'
    return 'Оформите банковскую рассрочку, чтобы оплатить покупку частями и без переплаты процентов. Можно оформить как с первым взносом, так и без него'
  }

  if (slug === 'edinorazovyi-platez') {
    return 'Совершите единоразовый платёж с помощью банковской карты, СБП или кошельков популярных сервисов'
  }

  return ''
}

function getStep1ImgSrc(slug: string) {
  if (slug === 'kredit') return kreditImg
  if (slug === 'rassrocka') return rassrockaImg
  if (slug === 'edinorazovyi-platez') return paymentImg
  return ''
}

// Цена СМС-информирования — из настроек партнёра (200 или 250 руб/мес), приходит в счёте
const smsUpsalePrice = computed(() => invoice.value?.upsale_prices?.sms_notification ?? 200)

function getUpsaleLabel(key: UpsaleKey) {
  const labels: Record<UpsaleKey, string> = {
    sms_notification: `Хочу получать напоминания о предстоящих платежах для спокойствия и оплаты без просрочек (${smsUpsalePrice.value} руб/мес)`,
    extended_insurance:
      'Хочу оформить страховку на случай, если не смогу найти работу после окончания обучения (2500 руб)',
    installment_conditions: 'Согласен с условиями рассрочки',
    basic_insurance: 'Базовая страховка',
    premium_insurance: 'Премиум страховка',
    subsidized_installment:
      'Переходя на следующий этап оформления, вы соглашаетесь с условиями субсидированной рассрочки.'
  }

  return labels[key]
}

function getUpsaleTooltip1(key: UpsaleKey) {
  const tooltips: Record<UpsaleKey, string> = {
    sms_notification: `Стоимость услуги СМС-информирования составляет ${smsUpsalePrice.value} рублей в месяц. Итоговая сумма услуги будут добавлена к общей стоимости покупки.`,
    extended_insurance:
      'Стоимость страховки составляет 2500 рублей, которые будут добавлены к общей стоимости покупки.',
    installment_conditions: '',
    basic_insurance: '',
    premium_insurance: '',
    subsidized_installment:
      'Данная опция предполагает, что проценты по договору рассрочки перекладываются на покупателя. Итоговая стоимость покупки складывается из суммы первоначальной стоимости заказа и процентов за весь период рассрочки. За подробной информацией рекомендуем обращаться к вашему менеджеру.'
  }

  return tooltips[key]
}

function getUpsaleTooltip2(key: UpsaleKey) {
  const tooltips: Record<UpsaleKey, string> = {
    sms_notification: '',
    extended_insurance:
      'Внимание! Сейчас страховку можно оформить со скидкой 50%. После завершения процесса оплаты приобрести страховку будет уже нельзя!',
    installment_conditions: '',
    basic_insurance: '',
    premium_insurance: '',
    subsidized_installment: ''
  }

  return tooltips[key]
}

function getUpsaleLink(key: UpsaleKey) {
  const links: Partial<Record<UpsaleKey, string>> = {
    sms_notification: 'https://terpay.ru/static/sms-rules.pdf'
  }

  return links[key] || ''
}

function getUpsaleLinkText(key: UpsaleKey) {
  const links: Partial<Record<UpsaleKey, string>> = {
    sms_notification: 'Правила предоставления услуги'
  }

  return links[key] || ''
}

// Оферта, привязанная к ссылке или счёту, приходит в offer_url позиции;
// если её нет — оферта юрлица по умолчанию.
const offerLink = computed(() => {
  const item = invoice.value?.items?.find((i) => i.offer_url || i.legal_entity?.offer)

  return item?.offer_url || item?.legal_entity?.offer || ''
})

const showOfferCheckbox = computed(() => {
  return !!invoice.value?.show_offer_on_payment_page && !!offerLink.value
})
</script>

<template>
  <div class="app-layout">
    <app-header :logo-src="invoice?.merchant?.logo_url ?? undefined" :logo-loading="pageLoader" />
    <app-banner
      v-if="invoice"
      :items="invoice.items"
      :total-price="invoice.price"
      :show-monthly-payment="invoice.show_monthly_payment"
      :monthly-payment="invoice.monthly_payment"
    />
    <div v-else-if="pageLoader" class="app-banner container skeleton-banner">
      <div class="skeleton-banner__meta">
        <div class="skeleton-block skeleton-block--banner-title" />
        <div class="skeleton-block skeleton-block--banner-subtitle" />
      </div>
      <div class="skeleton-block skeleton-block--banner-price" />
    </div>

    <ui-section
      :title="pageLoader ? '' : stepTitles.title"
      :subtitle="pageLoader ? undefined : stepTitles.subtitle"
    >
      <!-- Скелетон шага 1 -->
      <div class="section-items" v-if="pageLoader && currentStep === 1">
        <div class="grid-12">
          <div v-for="i in 3" :key="i" class="skeleton-card">
            <div class="skeleton-block skeleton-card__icon" />
            <div class="skeleton-card__body">
              <div class="skeleton-block skeleton-block--card-title" />
              <div class="skeleton-block skeleton-block--card-desc" />
              <div class="skeleton-block skeleton-block--card-desc skeleton-block--short" />
            </div>
          </div>
        </div>
      </div>

      <!-- Шаг 1 -->
      <div class="section-items" v-if="!pageLoader && currentStep === 1">
        <div class="grid-12">
          <PaymentOptionCard
            v-for="type in providerTypes"
            :key="type.slug"
            :title="getStep1Title(type.slug)"
            :imgSrc="getStep1ImgSrc(type.slug)"
            :description="getStep1Description(type.slug)"
            @select="selectType(type.slug)"
          />
        </div>
      </div>

      <!-- Шаг 2 — выбор типа рассрочки или кредитов -->
      <div class="section-items" v-if="currentStep === 2 && filteredProviders.length">
        <div class="grid-12">
          <Step2OptionCard
            v-for="(provider, index) in filteredProviders"
            :key="provider.id"
            :title="provider.title"
            :description="provider.description || provider.legal_info"
            :provider-image="provider.logo"
            :is-credit="selectedType === 'kredit'"
            :show-index="selectedType === 'kredit' ? index + 1 : undefined"
            :with-first-payment="
              selectedType === 'rassrocka' &&
              (provider.type === 'bnpl' || provider.credit_instrument_type === 'bnpl')
            "
            @select="selectProvider(provider)"
          />
        </div>

        <button v-if="canGoBack" class="btn-back" @click="goBack">
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M14.0001 7.33364H4.27612L7.80479 3.80497L6.86212 2.8623L1.72412 8.0003L6.86212 13.1383L7.80479 12.1956L4.27612 8.66697H14.0001V7.33364Z"
              fill="#262626"
            />
          </svg>
          <span>Назад</span>
        </button>
      </div>

      <!-- Шаг 3 — РАССРОЧКА / BNPL: выбор срока партнёрского офера (Т-Банк) -->
      <div
        class="section-items"
        v-if="currentStep === 3 && selectedType === 'rassrocka' && selectedBnplOption && showPartnerTerms"
      >
        <div class="partner-terms">
          <h2 class="partner-terms__title">Выберите комфортный срок и платёж для вашей рассрочки</h2>
          <p class="partner-terms__subtitle">После выбора срока вы перейдёте к заполнению основной анкеты. Обычно это занимает не более 3-5 минут.</p>

          <PartnerTermCard
            v-for="term in partnerTerms"
            :key="term.id"
            :term-months="term.term_months"
            :monthly-payment="term.monthly_payment"
            @select="choosePartnerTerm(term)"
          />
        </div>

        <button class="btn-back" @click="closePartnerTerms">
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M14.0001 7.33364H4.27612L7.80479 3.80497L6.86212 2.8623L1.72412 8.0003L6.86212 13.1383L7.80479 12.1956L4.27612 8.66697H14.0001V7.33364Z"
              fill="#262626"
            />
          </svg>
          <span>Назад</span>
        </button>
      </div>

      <!-- Шаг 3 — РАССРОЧКА / BNPL -->
      <div
        class="section-items"
        v-if="currentStep === 3 && selectedType === 'rassrocka' && selectedBnplOption && !showPartnerTerms"
      >
        <div class="grid-12">
          <Step2OptionCard
            v-for="(provider, index) in filteredProviders"
            :key="provider.id"
            :title="provider.title"
            :description="provider.description || provider.legal_info"
            :provider-image="provider.logo"
            :is-credit="true"
            :show-index="index + 1"
            @select="selectProvider(provider)"
          />
          <Step2OptionCard
            v-if="partnerOffer && selectedBnplOption.type === 'installment'"
            :title="partnerOffer.bank_name || partnerOffer.title"
            :description="partnerOffer.description ?? undefined"
            :provider-image="partnerOffer.logo_url ?? undefined"
            :is-credit="true"
            :show-index="filteredProviders.length + 1"
            @select="startPartnerOffer"
          />
          <Step2OptionCard
            v-if="externalLink"
            :description="externalLink.desc ?? undefined"
            :provider-image="externalLink.logo_url ?? undefined"
            :is-credit="true"
            :show-index="filteredProviders.length + (partnerOffer && selectedBnplOption.type === 'installment' ? 2 : 1)"
            @select="openExternalLink"
          />
        </div>

        <button v-if="canGoBack" class="btn-back" @click="goBack">
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M14.0001 7.33364H4.27612L7.80479 3.80497L6.86212 2.8623L1.72412 8.0003L6.86212 13.1383L7.80479 12.1956L4.27612 8.66697H14.0001V7.33364Z"
              fill="#262626"
            />
          </svg>
          <span>Назад</span>
        </button>
      </div>

      <!-- Шаг 3 — ЕДИНОРАЗОВЫЙ ПЛАТЁЖ -->
      <div
        class="section-items"
        v-if="
          currentStep === 3 && selectedType === 'edinorazovyi-platez' && filteredProviders.length
        "
      >
        <div class="grid-12">
          <PaymentProviderCard
            v-for="provider in filteredProviders"
            :key="provider.id"
            :logo="provider.logo"
            :description="provider.buttons[0].description"
            @select="submitAction(provider.buttons[0])"
          />
        </div>

        <div class="grid-12">
          <SwitchField
            v-for="key in visibleUpsales"
            :key="key"
            v-model="upsalesState[key]"
            :label="getUpsaleLabel(key)"
            :tooltipText1="getUpsaleTooltip1(key)"
            :tooltipText2="getUpsaleTooltip2(key)"
            :tooltipLink="getUpsaleLink(key)"
            :tooltipLinkText="getUpsaleLinkText(key)"
            :disabled="UPSALE_META[key].disabled"
          />

          <SwitchField
            v-if="showOfferCheckbox"
            v-model="offerAccepted"
            :error="offerError"
            label="Я соглашаюсь с условиями оферты"
            tooltipText1="Перед продолжением ознакомьтесь с документом."
            :tooltipLink="offerLink"
            tooltipLinkText="Открыть оферту"
            @update:modelValue="offerError = false"
          />
        </div>

        <button v-if="canGoBack" class="btn-back" @click="goBack">
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M14.0001 7.33364H4.27612L7.80479 3.80497L6.86212 2.8623L1.72412 8.0003L6.86212 13.1383L7.80479 12.1956L4.27612 8.66697H14.0001V7.33364Z"
              fill="#262626"
            />
          </svg>
          <span>Назад</span>
        </button>
      </div>

      <!-- Шаг 3 — КРЕДИТ -->
      <div
        class="section-items"
        v-if="currentStep === 3 && selectedType === 'kredit' && selectedProvider"
      >
        <div class="grid-12">
          <ProviderButtonCard
            v-for="button in selectedProvider.buttons"
            :key="button.id"
            :title="button.title"
            :subtitle="button.description"
            :monthlyPayment="button.monthly_payment"
            @submit="submitAction(button)"
          />
        </div>

        <div class="grid-12">
          <SwitchField
            v-for="key in visibleUpsales"
            :key="key"
            v-model="upsalesState[key]"
            :label="getUpsaleLabel(key)"
            :tooltipText1="getUpsaleTooltip1(key)"
            :tooltipText2="getUpsaleTooltip2(key)"
            :tooltipLink="getUpsaleLink(key)"
            :tooltipLinkText="getUpsaleLinkText(key)"
            :disabled="UPSALE_META[key].disabled"
          />

          <SwitchField
            v-if="showOfferCheckbox"
            v-model="offerAccepted"
            :error="offerError"
            label="Я соглашаюсь с условиями оферты"
            tooltipText1="Перед продолжением ознакомьтесь с документом."
            :tooltipLink="offerLink"
            tooltipLinkText="Открыть оферту"
            @update:modelValue="offerError = false"
          />
        </div>

        <button v-if="canGoBack" class="btn-back" @click="goBack">
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M14.0001 7.33364H4.27612L7.80479 3.80497L6.86212 2.8623L1.72412 8.0003L6.86212 13.1383L7.80479 12.1956L4.27612 8.66697H14.0001V7.33364Z"
              fill="#262626"
            />
          </svg>
          <span>Назад</span>
        </button>
      </div>

      <!-- Шаг 4 — РАССРОЧКА -->
      <div
        class="section-items"
        v-if="currentStep === 4 && selectedType === 'rassrocka' && selectedProvider"
      >
        <div class="grid-12">
          <ProviderButtonCard
            v-for="button in selectedProvider.buttons"
            :key="button.id"
            :title="button.title"
            :subtitle="button.description"
            :monthlyPayment="button.monthly_payment"
            @submit="submitAction(button)"
          />
        </div>

        <div class="grid-12">
          <SwitchField
            v-for="key in visibleUpsales"
            :key="key"
            v-model="upsalesState[key]"
            :label="getUpsaleLabel(key)"
            :tooltipText1="getUpsaleTooltip1(key)"
            :tooltipText2="getUpsaleTooltip2(key)"
            :tooltipLink="getUpsaleLink(key)"
            :tooltipLinkText="getUpsaleLinkText(key)"
            :disabled="UPSALE_META[key].disabled"
            :error="key === 'subsidized_installment' ? installmentError : false"
            @update:modelValue="installmentError = false"
          />

          <SwitchField
            v-if="showOfferCheckbox"
            v-model="offerAccepted"
            :error="offerError"
            label="Я соглашаюсь с условиями оферты"
            tooltipText1="Перед продолжением ознакомьтесь с документом."
            :tooltipLink="offerLink"
            tooltipLinkText="Открыть оферту"
            @update:modelValue="offerError = false"
          />
        </div>

        <button v-if="canGoBack" class="btn-back" @click="goBack">
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M14.0001 7.33364H4.27612L7.80479 3.80497L6.86212 2.8623L1.72412 8.0003L6.86212 13.1383L7.80479 12.1956L4.27612 8.66697H14.0001V7.33364Z"
              fill="#262626"
            />
          </svg>
          <span>Назад</span>
        </button>
      </div>
    </ui-section>

    <ui-footer />
    <vpn-warning-banner />

    <Transition name="loader-fade">
      <div v-if="isLoading" class="loading-overlay">
        <div class="loading-content">
          <div class="loading-spinner"></div>
          <p class="loading-text">Создаём ссылку на оплату...</p>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style scoped lang="scss">
@keyframes skeleton-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.skeleton-block {
  border-radius: 8px;
  background: linear-gradient(90deg, #e8eaed 25%, #f5f6f7 50%, #e8eaed 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
}

.skeleton-banner {
  display: flex;
  justify-content: space-between;
  align-items: center;

  &__meta {
    display: flex;
    flex-direction: column;
    gap: 12px;
    flex: 1;
  }
}

.skeleton-block--banner-title {
  height: 32px;
  width: 55%;
  max-width: 420px;
  border-radius: 10px;
}
.skeleton-block--banner-subtitle {
  height: 20px;
  width: 35%;
  max-width: 260px;
}
.skeleton-block--banner-price {
  height: 36px;
  width: 120px;
  border-radius: 10px;
  flex-shrink: 0;
}

.skeleton-card {
  border-radius: 20px;
  padding: 20px;
  background: #f7f9fa;
  display: flex;
  align-items: center;
  gap: 20px;

  &__icon {
    width: 70px;
    height: 70px;
    border-radius: 12px;
    flex-shrink: 0;
  }

  &__body {
    display: flex;
    flex-direction: column;
    gap: 10px;
    flex: 1;
  }
}

.skeleton-block--card-title {
  height: 22px;
  width: 50%;
  border-radius: 6px;
}
.skeleton-block--card-desc {
  height: 14px;
  width: 90%;
  border-radius: 4px;
}
.skeleton-block--short {
  width: 65%;
}

.section-items {
  display: flex;
  flex-direction: column;
  row-gap: fluid(32, 24);
}

.grid-12 {
  display: grid;
  row-gap: 12px;
}

.btn-back {
  border-radius: 20px;
  width: 110px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f3f5f7;
  cursor: pointer;
  transition: background-color 0.2s;
  text-align: center;
  border: none;
  font-weight: 400;
  font-size: 16px;
  line-height: 150%;
  color: #262626;
  gap: 7px;
}

.btn-back:hover {
  opacity: 0.8;
}

.loading-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(2px);
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.loading-text {
  font-size: 18px;
  font-weight: 600;
  color: #262626;
  margin: 0;
  opacity: 80%;
}

.loading-spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #e0e0e0;
  border-top-color: #72a6ff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loader-fade-enter-active,
.loader-fade-leave-active {
  transition: opacity 0.2s ease;
}

.loader-fade-enter-from,
.loader-fade-leave-to {
  opacity: 0;
}
.partner-terms {
  display: flex;
  flex-direction: column;
  gap: 12px;

  &__title {
    margin: 0;
    font-size: 22px;
    font-weight: 700;
  }

  &__subtitle {
    margin: 0 0 8px;
    font-size: 14px;
    color: #6b7280;
  }
}
</style>
